import closeIcon from '@jetbrains/icons/close.svg'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import classNames from 'classnames'
import type {MouseEventHandler, MouseEvent} from 'react'
import * as React from 'react'
import {useCallback} from 'react'

import {getAgentTypeHref} from '../../../../../routes'
import {getCanViewAgentDetails} from '../../../../../selectors'
import {stringifyId} from '../../../../../types'
import AgentLink from '../../../../common/AgentLink/AgentLink'
import IconButton from '../../../../common/IconButton/IconButton'
import {STRING_DEFAULT_AGENT_POOL_ID} from '../../AgentPoolPage/AgentPoolPage.constants'
import {ExpandHandle} from '../../AgentPoolPage/IndentedExpandHandle/IndentedExpandHandle'
import type {AgentsTreeAgentTypeNode} from '../../AgentsSidebar/AgentsSidebar.types'

import {useMoveCloudImageToAgentPool} from './AgentListView.hooks'
import InstancesCounter from './InstancesCounter/InstancesCounter'
import InstancesDropdown from './InstancesDropdown/InstancesDropdown'

import styles from './AgentListView.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = AgentsTreeAgentTypeNode & {
  className?: string | null | undefined
  expandable?: boolean
  expanded?: boolean
  onClick?: MouseEventHandler
  removable?: boolean
}

const ANCHOR_CLASSNAME = 'dropdown-anchor'

export default function CloudAgentListView({
  id,
  title,
  osType,
  count,
  busyCount,
  children,
  className,
  expandable,
  expanded,
  onClick,
  removable,
}: Props) {
  const counter = (
    <InstancesCounter
      count={count}
      busyCount={busyCount}
      className={expandable ? ANCHOR_CLASSNAME : null}
    />
  )
  const onToggleExpand = useCallback(
    (e: MouseEvent) => {
      if (onClick != null && (e.target as Element)?.closest(`.${ANCHOR_CLASSNAME}`) == null) {
        onClick(e)
      }
    },
    [onClick],
  )
  const [moveCloudImageToDeaultPool, movingCloudImageToDeaultPool] = useMoveCloudImageToAgentPool(
    stringifyId(id),
    STRING_DEFAULT_AGENT_POOL_ID,
  )

  return (
    <div
      className={classNames(className, styles.agent, {[styles.expandable]: expandable})}
      onClick={onToggleExpand}
      role="presentation"
    >
      {expandable ? (
        <ExpandHandle expandable expanded={expanded} className={styles.expandHandle} />
      ) : null}
      <AgentLink
        isSakuraUI
        className={styles.link}
        name={title}
        {...(useAppSelector(getCanViewAgentDetails)
          ? {
              href: getAgentTypeHref(id),
              typeId: id,
              osType,
            }
          : {})}
      />
      {count > 0 ? (
        <InstancesDropdown instances={children} agentTypeId={id} anchor={counter} />
      ) : (
        counter
      )}
      <div className={styles.split} />
      {removable ? (
        movingCloudImageToDeaultPool ? (
          <LoaderInline className={styles.loader} />
        ) : (
          <IconButton
            onClick={moveCloudImageToDeaultPool as MouseEventHandler<HTMLButtonElement>}
            icon={closeIcon}
            className={styles.unassignButton}
            title="Move to the Default agent pool"
          />
        )
      ) : null}
    </div>
  )
}
